import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Radio,
  Globe,
  Edit3,
  FileText,
  LogOut,
  CheckCircle2,
  ArrowUpRight,
  Bell,
  Clock,
  ShieldCheck,
  Youtube,
  AlertCircle,
  ExternalLink,
  X,
  Info,
  MapPin,
  Music,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { FEATURES, ROLE_LABELS, ROLE_ICON_COMPONENTS, getFeaturesForRoles, isArtistRole } from '../../lib/rolePermissions';
import { fetchUserNotifications, formatNotificationTime, respondToCollabRequest, formatSpotifyArtistUrl, formatExternalUrl } from '../../lib/notificationService';
import { fetchUserReleases } from '../../lib/distributionService';
import {
  fetchUserOacRequest,
  fetchArtistQualifyingReleases,
  submitOacRequest,
  cancelOacRequest,
  validateYouTubeTopicUrl,
  validateYouTubeChannelUrl,
  OAC_STATUS_LABELS,
  SENRITSUX_OAC_DISCLAIMERS,
} from '../../lib/oacService';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import logo from '../../assets/SenritsuXwordmarkwhite.png';
import '../../styles/DashboardPage.css';

/** Sidebar navigation items */
const SIDEBAR_ITEMS = [
  { feature: FEATURES.DASHBOARD, icon: <LayoutDashboard size={17} />, label: 'Overview', path: '/dashboard' },
  { feature: FEATURES.DISTRIBUTION, icon: <Radio size={17} />, label: 'Distribution', path: '/distribution' },
  { feature: FEATURES.ARTIST_WEB, icon: <Globe size={17} />, label: 'Artist Web', path: '/artist-web' },
  { feature: FEATURES.DRAFTS, icon: <FileText size={17} />, label: 'Drafts', path: '/drafts' },
];

export default function DashboardPage() {
  const { user, profile, roles, signOut, loading, refreshUserData } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifs, setLoadingNotifs] = useState(true);
  const [userReleases, setUserReleases] = useState([]);
  const [loadingReleases, setLoadingReleases] = useState(true);
  const [followerCount, setFollowerCount] = useState(0);
  const [avatarError, setAvatarError] = useState(false);

  // OAC State
  const [oacRequest, setOacRequest] = useState(null);
  const [loadingOac, setLoadingOac] = useState(true);
  const [qualifyingReleases, setQualifyingReleases] = useState([]);
  const [showOacModal, setShowOacModal] = useState(false);
  const [oacForm, setOacForm] = useState({
    releaseId: '',
    youtubeTopicUrl: '',
    youtubeChannelUrl: '',
  });
  const [oacChecklist, setOacChecklist] = useState({
    ownsChannel: false,
    representsArtist: false,
    hasOfficialRelease: false,
    topicUrlCorrect: false,
    threeVideosNonShorts: false,
    noGuaranteeUnderstood: false,
    youtubeApprovalUnderstood: false,
  });
  const [oacSubmitting, setOacSubmitting] = useState(false);
  const [oacError, setOacError] = useState('');
  const [oacSuccess, setOacSuccess] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  const userFeatures = getFeaturesForRoles(roles);

  // 1. Initial data fetching
  useEffect(() => {
    let isMounted = true;

    async function loadData() {
      if (!user?.id) return;
      try {
        if (typeof refreshUserData === 'function') {
          refreshUserData().catch(() => {});
        }
        const [notifData, releaseData, followsRes] = await Promise.all([
          fetchUserNotifications(user.id),
          fetchUserReleases(user.id),
          isSupabaseConfigured()
            ? supabase
                .from('artist_follows')
                .select('id', { count: 'exact', head: true })
                .eq('artist_user_id', user.id)
            : Promise.resolve({ count: 0 }),
        ]);

        // Dynamically resolve sender social links for incoming collab requests if missing in payload
        if (notifData && isSupabaseConfigured()) {
          const senderIds = notifData
            .filter(
              (n) =>
                n.type === 'collab_request' &&
                n.data?.sender_id &&
                (!n.data?.spotify_link || !n.data?.youtube || !n.data?.instagram)
            )
            .map((n) => n.data.sender_id);

          if (senderIds.length > 0) {
            try {
              const { data: senderProfiles } = await supabase
                .from('profiles')
                .select('id, spotify_link, youtube, instagram')
                .in('id', [...new Set(senderIds)]);

              if (senderProfiles?.length) {
                const spMap = Object.fromEntries(senderProfiles.map((p) => [p.id, p]));
                notifData.forEach((n) => {
                  if (n.type === 'collab_request' && n.data?.sender_id && spMap[n.data.sender_id]) {
                    const sp = spMap[n.data.sender_id];
                    if (!n.data.spotify_link && sp.spotify_link) {
                      n.data.spotify_link = formatSpotifyArtistUrl(sp.spotify_link);
                    }
                    if (!n.data.youtube && sp.youtube) {
                      n.data.youtube = formatExternalUrl(sp.youtube, 'youtube.com');
                    }
                    if (!n.data.instagram && sp.instagram) {
                      n.data.instagram = formatExternalUrl(sp.instagram, 'instagram.com');
                    }
                  }
                });
              }
            } catch {}
          }
        }

        if (isMounted) {
          setNotifications(notifData || []);
          // Deduplicate releases to eliminate duplicate rows
          const seenRelIds = new Set();
          const uniqueReleases = (releaseData || []).filter((r) => {
            if (!r || !r.id) return false;
            if (seenRelIds.has(r.id)) return false;
            seenRelIds.add(r.id);
            return true;
          });
          setUserReleases(uniqueReleases);
          setFollowerCount(followsRes?.count ?? 0);
        }

        // Fetch OAC status and qualifying releases
        try {
          const [oacRes, qualRes] = await Promise.all([
            fetchUserOacRequest(user.id),
            fetchArtistQualifyingReleases(user.id),
          ]);
          if (isMounted) {
            setOacRequest(oacRes.data || null);
            setQualifyingReleases(qualRes.data || []);
            if (qualRes.data?.length > 0) {
              setOacForm((prev) => ({
                ...prev,
                releaseId: prev.releaseId || qualRes.data[0].id,
              }));
            }
          }
        } catch (oacErr) {
          console.warn('[DashboardPage] OAC data load warning:', oacErr);
        }
      } catch (err) {
        console.warn('[DashboardPage] Data load notice:', err);
      } finally {
        if (isMounted) {
          setLoadingNotifs(false);
          setLoadingReleases(false);
          setLoadingOac(false);
        }
      }
    }

    loadData();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // 2. Realtime listener for follower count updates
  useEffect(() => {
    if (!user?.id || !isSupabaseConfigured()) return;

    let isMounted = true;
    const channelTopic = `dashboard_follows_${user.id}`;

    const existing = supabase.getChannels().find((ch) => ch.topic === `realtime:${channelTopic}` || ch.topic === channelTopic);
    if (existing) {
      supabase.removeChannel(existing);
    }

    const followsChannel = supabase
      .channel(`dashboard_follows_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'artist_follows',
          filter: `artist_user_id=eq.${user.id}`,
        },
        async () => {
          try {
            const { count } = await supabase
              .from('artist_follows')
              .select('id', { count: 'exact', head: true })
              .eq('artist_user_id', user.id);
            if (isMounted && typeof count === 'number') {
              setFollowerCount(count);
            }
          } catch (err) {
            console.warn('[DashboardPage] Realtime follower count notice:', err);
          }
        }
      )
      .subscribe();

    return () => {
      isMounted = false;
      if (followsChannel) {
        supabase.removeChannel(followsChannel);
      }
    };
  }, [user?.id]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleCollabAction = async (notif, action) => {
    try {
      const collabId = notif.data?.collab_id;
      const senderId = notif.data?.sender_id;
      await respondToCollabRequest({
        collabId,
        notificationId: notif.id,
        action,
        senderId,
        recipientUser: user,
        recipientProfile: profile,
      });

      setNotifications((prev) =>
        prev.map((item) =>
          item.id === notif.id
            ? {
                ...item,
                is_read: true,
                data: { ...(item.data || {}), status: action },
              }
            : item
        )
      );
    } catch (err) {
      console.warn('[DashboardPage] Collab action notice:', err);
    }
  };

  if (loading) {
    return (
      <div className="auth-skeleton">
        <div className="auth-skeleton-card">
          <div className="auth-skeleton-line medium" />
          <div className="auth-skeleton-line short" />
          <div className="auth-skeleton-line tall" />
        </div>
      </div>
    );
  }

  const displayName = profile?.stage_name || profile?.name || user?.user_metadata?.stage_name || user?.email?.split('@')[0] || 'Creator';
  const avatarInitial = (displayName[0] || '?').toUpperCase();
  const unreadNotifsCount = notifications.filter((n) => !n.is_read).length;
  const totalReleasesCount = Math.max(userReleases.length, profile?.total_releases || 0);
  const totalStreamsCount = profile?.total_streams != null ? Number(profile.total_streams).toLocaleString() : '0';

  return (
    <div className="dashboard-page">
      {/* Top Header */}
      <header className="dashboard-header">
        <div className="dashboard-header-left">
          <Link to="/">
            <img src={logo} alt="SenritsuX" className="dashboard-logo" />
          </Link>
        </div>
        <div className="dashboard-header-right">
          <span className="dashboard-username">{displayName}</span>
          <div className="dashboard-avatar">
            {profile?.avatar_url && !avatarError ? (
              <img src={profile.avatar_url} alt={displayName} onError={() => setAvatarError(true)} />
            ) : (
              avatarInitial
            )}
          </div>
          <button className="dashboard-header-logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <div className="dashboard-body">
        {/* Left Sidebar Navigation */}
        <aside className="dashboard-sidebar">
          <div className="sidebar-section-title">NAVIGATION</div>
          {SIDEBAR_ITEMS.filter((item) => userFeatures.includes(item.feature)).map((item) => {
            const isActive = location.pathname === item.path || (item.feature === FEATURES.DASHBOARD && location.pathname === '/dashboard');
            return (
              <Link
                key={item.feature}
                to={item.path}
                className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
              >
                <span className="sidebar-nav-icon">{item.icon}</span>
                <span>{item.label}</span>
              </Link>
            );
          })}

          <div className="sidebar-section-title" style={{ marginTop: '0.8rem' }}>ACCOUNT</div>
          <Link
            to="/edit-profile"
            className={`sidebar-nav-item ${location.pathname === '/edit-profile' ? 'active' : ''}`}
          >
            <span className="sidebar-nav-icon"><Edit3 size={17} /></span>
            <span>Edit Profile &amp; Roles</span>
          </Link>
          <button className="sidebar-nav-item" onClick={handleLogout}>
            <span className="sidebar-nav-icon"><LogOut size={17} /></span>
            <span>Logout</span>
          </button>
        </aside>

        {/* Main Dashboard Control Center */}
        <main className="dashboard-main">
          <div className="dashboard-grid-layout">
            {/* Left Column: Welcome Card, Stats Row, Notifications */}
            <div className="dashboard-left-col">
              {/* 1. Welcome Card */}
              <div className="dashboard-card dashboard-welcome-card">
                <h1 className="dashboard-welcome-title">
                  Welcome back, {displayName}
                  {profile?.is_verified && (
                    <span title="Verified Artist" style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: '0.5rem' }}>
                      <ShieldCheck size={18} color="#d9fb06" />
                    </span>
                  )}
                </h1>

                <div className="dashboard-welcome-badges">
                  {roles.length > 0 ? (
                    roles.map((r) => {
                      const RoleIconComp = ROLE_ICON_COMPONENTS[r];
                      return (
                        <span key={r} className="dashboard-badge">
                          {RoleIconComp && <RoleIconComp size={13} color="#d9fb06" />}
                          <span>{ROLE_LABELS[r] || r}</span>
                        </span>
                      );
                    })
                  ) : (
                    <span className="dashboard-badge">
                      <span>Creator</span>
                    </span>
                  )}

                  {profile?.city && (
                    <span className="dashboard-badge">
                      <MapPin size={12} color="#d9fb06" />
                      <span>{profile.city.toUpperCase()}</span>
                    </span>
                  )}

                  {(profile?.genre || profile?.secondary_genre) && (
                    <span className="dashboard-badge">
                      <Music size={12} color="#d9fb06" />
                      <span>
                        {profile.genre ? profile.genre.toUpperCase() : ''}
                        {profile.secondary_genre ? ` · ${profile.secondary_genre.toUpperCase()}` : ''}
                      </span>
                    </span>
                  )}
                </div>
              </div>

              {/* 2. Three Statistics Cards: Followers, Streams, Releases */}
              <div className="dashboard-stats-row">
                <div className="dashboard-stat-card">
                  <div className="dashboard-stat-label">FOLLOWERS</div>
                  <div className="dashboard-stat-value highlight-lime">
                    {followerCount.toLocaleString()}
                  </div>
                </div>

                <div className="dashboard-stat-card">
                  <div className="dashboard-stat-label">TOTAL STREAMS</div>
                  <div className="dashboard-stat-value">
                    {totalStreamsCount}
                  </div>
                </div>

                <div className="dashboard-stat-card">
                  <div className="dashboard-stat-label">TOTAL RELEASES</div>
                  <div className="dashboard-stat-value">
                    {totalReleasesCount.toLocaleString()}
                  </div>
                </div>
              </div>

              {/* 3. Notifications & Inquiries Panel */}
              <div className="dashboard-card dashboard-notifs-panel">
                <div className="dashboard-panel-header">
                  <h2 className="dashboard-panel-title">
                    <Bell size={16} color="#d9fb06" />
                    <span>Notifications &amp; Inquiries</span>
                  </h2>
                  <span className="dashboard-lime-badge">
                    {unreadNotifsCount} New
                  </span>
                </div>

                {loadingNotifs ? (
                  <div className="dashboard-empty-text">Loading notifications...</div>
                ) : notifications.length > 0 ? (
                  <div className="dashboard-notifs-list">
                    {notifications.map((n) => (
                      <div key={n.id} className="dashboard-notif-row">
                        <div className="dashboard-notif-top">
                          <span className="dashboard-notif-title">
                            {n.title || 'Notification'}
                          </span>
                          <span className="dashboard-notif-time">
                            <Clock size={11} /> {formatNotificationTime(n.created_at)}
                          </span>
                        </div>
                        <p className="dashboard-notif-desc">
                          {n.message}
                        </p>

                        {/* Interactive Collaboration Actions */}
                        {n.type === 'collab_request' && (
                          <div style={{ marginTop: '0.45rem', paddingTop: '0.45rem', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                            {n.title === 'COLLABORATION ACCEPTED' || n.data?.action === 'accepted' ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                                <span style={{ color: '#22c55e', fontSize: '0.78rem', fontWeight: 700 }}>
                                  ✓ COLLABORATION ACCEPTED
                                </span>
                                {n.data?.partner_instagram && (
                                  <a
                                    href={n.data.partner_instagram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '0.35rem',
                                      background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                                      color: '#fff',
                                      padding: '0.3rem 0.75rem',
                                      borderRadius: '6px',
                                      fontSize: '0.75rem',
                                      fontWeight: 700,
                                      textDecoration: 'none',
                                      width: 'fit-content',
                                    }}
                                  >
                                    Message on Instagram ↗
                                  </a>
                                )}
                              </div>
                            ) : n.title === 'COLLABORATION DECLINED' || n.data?.action === 'denied' || n.data?.action === 'declined' ? (
                              <div>
                                <span style={{ color: '#ef4444', fontSize: '0.78rem', fontWeight: 700 }}>
                                  ✕ COLLABORATION DECLINED
                                </span>
                              </div>
                            ) : (
                              <div>
                                {n.data?.status === 'accepted' ? (
                                  <span style={{ color: '#22c55e', fontSize: '0.75rem', fontWeight: 700 }}>
                                    ✓ Accepted
                                  </span>
                                ) : n.data?.status === 'denied' || n.data?.status === 'declined' ? (
                                  <span style={{ color: '#ef4444', fontSize: '0.75rem', fontWeight: 700 }}>
                                    ✕ Declined
                                  </span>
                                ) : (
                                  <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.3rem' }}>
                                    <button
                                      type="button"
                                      onClick={() => handleCollabAction(n, 'accepted')}
                                      style={{
                                        background: '#22c55e',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '0.25rem 0.65rem',
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                      }}
                                    >
                                      ACCEPT
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleCollabAction(n, 'denied')}
                                      style={{
                                        background: 'rgba(255, 255, 255, 0.12)',
                                        color: '#fff',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        borderRadius: '4px',
                                        padding: '0.25rem 0.65rem',
                                        fontSize: '0.72rem',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                      }}
                                    >
                                      DENY
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="dashboard-empty-text">
                    No notifications or inquiries at this time.
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Release Distribution + Submit Next Release Button */}
            <div className="dashboard-right-col">
              <div className="dashboard-card dashboard-releases-panel">
                <div className="dashboard-panel-header">
                  <h2 className="dashboard-panel-title">
                    <Radio size={16} color="#d9fb06" />
                    <span>Release Distribution</span>
                  </h2>
                </div>

                {loadingReleases ? (
                  <div className="dashboard-empty-text">Loading releases...</div>
                ) : userReleases.length > 0 ? (
                  <div className="dashboard-releases-list">
                    {userReleases.map((rel) => (
                      <div key={rel.id} className="dashboard-release-item">
                        <div>
                          <div className="dashboard-release-title">
                            {rel.title}
                          </div>
                          <div className="dashboard-release-meta">
                            <span>{rel.releaseType?.toUpperCase() || 'SINGLE'}</span>
                            <span> • {rel.trackCount} {rel.trackCount === 1 ? 'Track' : 'Tracks'}</span>
                            {rel.releaseDate && <span> • Release: {rel.releaseDate}</span>}
                          </div>
                          <div className="dashboard-release-reg">
                            SenritsuX Reg: {rel.senritsuxRegistrationNumber || rel.referenceCode || 'SX-2026-REGISTERED'}
                          </div>
                        </div>

                        <div>
                          <span
                            className={`dashboard-release-status-badge ${
                              rel.status === 'live' ? 'live' : 'review'
                            }`}
                          >
                            {rel.status === 'submitted' ? 'Under A&R Review' : rel.status}
                          </span>
                          <div className="dashboard-release-isrc">
                            ISRC: {rel.isrc ? rel.isrc : 'Not assigned yet'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="dashboard-empty-text">
                    No releases distributed yet.
                  </div>
                )}

                {/* Prominent lime Submit Next Release Button */}
                <Link to="/distribution?mode=new" className="dashboard-submit-next-btn">
                  <span>Submit Next Release</span>
                  <ArrowUpRight size={15} />
                </Link>
              </div>
            </div>

            {/* Full-width Lower Section: Official Artist Channel (OAC) Hub */}
            <div className="dashboard-card dashboard-oac-card">
              <div className="dashboard-panel-header" style={{ marginBottom: '0.6rem' }}>
                <h2 className="dashboard-panel-title">
                  <Youtube size={18} color="#ff0000" />
                  <span>Official Artist Channel (OAC)</span>
                </h2>
                {oacRequest && (
                  <span
                    style={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      padding: '0.2rem 0.6rem',
                      borderRadius: '4px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      background:
                        oacRequest.status === 'approved'
                          ? 'rgba(34, 197, 94, 0.2)'
                          : oacRequest.status === 'rejected'
                          ? 'rgba(239, 68, 68, 0.2)'
                          : 'rgba(217, 251, 6, 0.15)',
                      color:
                        oacRequest.status === 'approved'
                          ? '#22c55e'
                          : oacRequest.status === 'rejected'
                          ? '#ef4444'
                          : '#d9fb06',
                      border: `1px solid ${
                        oacRequest.status === 'approved'
                          ? 'rgba(34, 197, 94, 0.4)'
                          : oacRequest.status === 'rejected'
                          ? 'rgba(239, 68, 68, 0.4)'
                          : 'rgba(217, 251, 6, 0.3)'
                      }`,
                    }}
                  >
                    {OAC_STATUS_LABELS[oacRequest.status] || oacRequest.status}
                  </span>
                )}
              </div>

              {loadingOac ? (
                <div className="dashboard-empty-text">Checking OAC eligibility...</div>
              ) : oacRequest && (oacRequest.status === 'pending' || oacRequest.status === 'under_review') ? (
                /* Request submitted / under review */
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: '#d9fb06', fontWeight: 600, fontSize: '0.88rem' }}>
                    <Clock size={15} /> OAC Request Under Review
                  </div>
                  <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.45, margin: '0 0 0.8rem' }}>
                    Your request has been submitted to the SenritsuX review desk. Our team is verifying your YouTube channel against internal screening requirements before submitting into the music distributor pipeline for YouTube processing.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.6rem', fontSize: '0.76rem', background: 'rgba(0,0,0,0.2)', padding: '0.7rem', borderRadius: '6px', marginBottom: '0.8rem' }}>
                    <div>
                      <span style={{ color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: '0.15rem' }}>Release:</span>
                      <strong style={{ color: '#fff' }}>{oacRequest.releases?.title || 'SenritsuX Release'}</strong>
                    </div>
                    <div>
                      <span style={{ color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: '0.15rem' }}>YouTube Channel:</span>
                      <a href={oacRequest.youtube_channel_url} target="_blank" rel="noopener noreferrer" style={{ color: '#d9fb06', textDecoration: 'underline', wordBreak: 'break-all', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                        {oacRequest.youtube_channel_url} <ExternalLink size={11} />
                      </a>
                    </div>
                    <div>
                      <span style={{ color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: '0.15rem' }}>Submitted:</span>
                      <span style={{ color: '#fff' }}>{new Date(oacRequest.submitted_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  {oacRequest.status === 'pending' && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to cancel this pending OAC request?')) {
                          try {
                            await cancelOacRequest(oacRequest.id, user.id);
                            const updated = await fetchUserOacRequest(user.id);
                            setOacRequest(updated.data);
                          } catch (err) {
                            alert(err.message || 'Failed to cancel request');
                          }
                        }
                      }}
                      style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)', borderRadius: '6px', padding: '0.3rem 0.75rem', fontSize: '0.74rem', cursor: 'pointer' }}
                    >
                      Cancel Request
                    </button>
                  )}
                </div>
              ) : oacRequest && oacRequest.status === 'approved' ? (
                /* Approved / Processed */
                <div style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: '10px', padding: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem', color: '#22c55e', fontWeight: 700, fontSize: '0.9rem' }}>
                    <CheckCircle2 size={16} /> OAC Request Approved &amp; Processed
                  </div>
                  <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.45, margin: '0' }}>
                    Your Official Artist Channel request has been screened and approved by SenritsuX, and forwarded into the music distributor delivery pipeline for YouTube OAC upgrade and topic track merging. Final upgrading on YouTube operates per partner delivery schedules.
                  </p>
                </div>
              ) : (
                /* Standard screening info matching 16.webp */
                <div>
                  <h3 className="dashboard-oac-subtitle">
                    Want to request an Official Artist Channel on YouTube?
                  </h3>
                  <p className="dashboard-oac-text">
                    Before you request it, make sure you have an official SenritsuX release on YouTube and a YouTube channel with at least 3 public non-Shorts videos. These are SenritsuX's screening requirements; final eligibility and approval are subject to YouTube's policies and applicable distributor process.
                  </p>
                  <button
                    type="button"
                    className="dashboard-oac-btn"
                    onClick={() => {
                      setOacError('');
                      setShowOacModal(true);
                    }}
                  >
                    <Youtube size={15} />
                    <span>Request OAC</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* OAC REQUEST FORM MODAL */}
      {showOacModal && (
        <div className="avatar-modal-backdrop" role="dialog" aria-modal="true" style={{ zIndex: 1000 }}>
          <div className="avatar-modal-card" style={{ maxWidth: '640px', width: '95%', maxHeight: '90vh', overflowY: 'auto', textAlign: 'left', padding: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Youtube size={22} color="#ff0000" />
                <h3 className="avatar-modal-title" style={{ margin: 0, fontSize: '1.25rem' }}>
                  Request Official Artist Channel
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowOacModal(false)}
                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: '0.2rem' }}
                aria-label="Close modal"
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ background: 'rgba(217, 251, 6, 0.08)', border: '1px solid rgba(217, 251, 6, 0.2)', borderRadius: '8px', padding: '0.85rem', marginBottom: '1.2rem', fontSize: '0.82rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>
              <strong style={{ color: '#d9fb06', display: 'block', marginBottom: '0.2rem' }}>
                SenritsuX Screening Notice:
              </strong>
              Submitting this form sends your channel into the SenritsuX review desk for internal screening. Final OAC verification and approval are executed through YouTube and the applicable music distribution partner process.
            </div>

            {oacError && (
              <div className="auth-global-error" style={{ marginBottom: '1rem' }}>
                <AlertCircle size={16} />
                <span>{oacError}</span>
              </div>
            )}

            {oacSuccess && (
              <div className="auth-global-success" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <CheckCircle2 size={16} color="#d9fb06" />
                <span>{oacSuccess}</span>
              </div>
            )}

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setOacError('');
                setOacSuccess('');

                const isTopicValid = validateYouTubeTopicUrl(oacForm.youtubeTopicUrl);
                if (!isTopicValid.valid) {
                  setOacError(isTopicValid.error);
                  return;
                }

                const isChannelValid = validateYouTubeChannelUrl(oacForm.youtubeChannelUrl);
                if (!isChannelValid.valid) {
                  setOacError(isChannelValid.error);
                  return;
                }

                const isChecklistComplete = Object.values(oacChecklist).every(Boolean);
                if (!isChecklistComplete) {
                  setOacError('Please confirm all checklist items before submitting.');
                  return;
                }

                setOacSubmitting(true);
                try {
                  const res = await submitOacRequest({
                    userId: user.id,
                    artistProfileId: profile?.id || null,
                    releaseId: oacForm.releaseId,
                    youtubeReleaseUrl: oacForm.youtubeTopicUrl,
                    youtubeChannelUrl: oacForm.youtubeChannelUrl,
                    checklistConfirmed: isChecklistComplete,
                  });

                  if (res?.success) {
                    setOacSuccess('Your OAC request has been submitted.');
                    const updated = await fetchUserOacRequest(user.id);
                    setOacRequest(updated.data);
                    setTimeout(() => {
                      setShowOacModal(false);
                      setOacSuccess('');
                    }, 1800);
                  }
                } catch (err) {
                  setOacError(err.message || 'Failed to submit OAC request. Please try again.');
                } finally {
                  setOacSubmitting(false);
                }
              }}
              noValidate
            >
              {/* Field A: Select Qualifying SenritsuX Release */}
              <div className="auth-form-group" style={{ marginBottom: '1.2rem' }}>
                <label className="auth-label" htmlFor="oac-release-select">
                  Select Official SenritsuX Release *
                </label>
                <select
                  id="oac-release-select"
                  className="auth-input"
                  value={oacForm.releaseId}
                  onChange={(e) => setOacForm({ ...oacForm, releaseId: e.target.value })}
                  style={{ appearance: 'auto', background: 'rgba(255,255,255,0.08)', color: '#fff' }}
                  disabled={oacSubmitting}
                >
                  {qualifyingReleases.length > 0 ? (
                    qualifyingReleases.map((rel) => (
                      <option key={rel.id} value={rel.id} style={{ background: '#1d1933', color: '#fff' }}>
                        {rel.title} ({rel.release_type?.toUpperCase()}) - {rel.senritsux_registration_number || 'Registered'}
                      </option>
                    ))
                  ) : userReleases.length > 0 ? (
                    userReleases.map((rel) => (
                      <option key={rel.id} value={rel.id} style={{ background: '#1d1933', color: '#fff' }}>
                        {rel.title} ({rel.releaseType?.toUpperCase()}) - {rel.senritsuxRegistrationNumber || 'Registered'}
                      </option>
                    ))
                  ) : (
                    <option value="" style={{ background: '#1d1933', color: '#fff' }}>
                      No releases available
                    </option>
                  )}
                </select>
                <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)', display: 'block', marginTop: '0.3rem' }}>
                  Only official releases distributed through SenritsuX are eligible.
                </span>
              </div>

              {/* Field B: YouTube Topic / Official Release Link */}
              <div className="auth-form-group" style={{ marginBottom: '1.2rem' }}>
                <label className="auth-label" htmlFor="oac-topic-url">
                  YouTube Topic / Official Release Link *
                </label>
                <input
                  id="oac-topic-url"
                  type="url"
                  className="auth-input"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={oacForm.youtubeTopicUrl}
                  onChange={(e) => setOacForm({ ...oacForm, youtubeTopicUrl: e.target.value })}
                  disabled={oacSubmitting}
                />
                <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', display: 'block', marginTop: '0.3rem' }}>
                  Paste the YouTube Topic/Art Track link for your SenritsuX-distributed release.
                </span>
              </div>

              {/* Field C: YouTube Channel Link */}
              <div className="auth-form-group" style={{ marginBottom: '1.5rem' }}>
                <label className="auth-label" htmlFor="oac-channel-url">
                  YouTube Channel Link *
                </label>
                <input
                  id="oac-channel-url"
                  type="text"
                  className="auth-input"
                  placeholder="https://www.youtube.com/@yourartist or /channel/UC..."
                  value={oacForm.youtubeChannelUrl}
                  onChange={(e) => setOacForm({ ...oacForm, youtubeChannelUrl: e.target.value })}
                  disabled={oacSubmitting}
                />
                <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', display: 'block', marginTop: '0.3rem' }}>
                  Provide the official YouTube channel that you own and manage. Supports @handle, /channel/, or /c/.
                </span>
              </div>

              {/* Screening Criterion Notice */}
              <div style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '0.9rem', marginBottom: '1.2rem' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <Info size={16} color="#d9fb06" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.5 }}>
                    <strong style={{ color: '#fff' }}>SenritsuX Screening Criterion:</strong>
                    <br />
                    {SENRITSUX_OAC_DISCLAIMERS.THREE_VIDEO_NOTE}
                  </div>
                </div>
              </div>

              {/* Verification Checklist */}
              <div style={{ marginBottom: '1.5rem' }}>
                <span className="auth-label" style={{ marginBottom: '0.6rem', display: 'block' }}>
                  Verification Checklist *
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {[
                    { key: 'ownsChannel', label: 'I own/manage this YouTube channel.' },
                    { key: 'representsArtist', label: 'My channel represents my artist identity/music.' },
                    { key: 'hasOfficialRelease', label: 'I have an official SenritsuX-distributed release on YouTube.' },
                    { key: 'topicUrlCorrect', label: 'I have provided the correct YouTube Topic/official release link.' },
                    { key: 'threeVideosNonShorts', label: 'My YouTube channel has at least 3 public non-Shorts videos. (SenritsuX OAC request requirement)' },
                    { key: 'noGuaranteeUnderstood', label: 'I understand that submitting this request does not guarantee OAC approval.' },
                    { key: 'youtubeApprovalUnderstood', label: 'I understand that final OAC eligibility/approval is subject to YouTube and the applicable music-distribution process.' },
                  ].map(({ key, label }) => {
                    const isChecked = !!oacChecklist[key];
                    return (
                      <label
                        key={key}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '0.6rem',
                          cursor: oacSubmitting ? 'default' : 'pointer',
                          fontSize: '0.82rem',
                          color: isChecked ? '#fff' : 'rgba(255,255,255,0.7)',
                          lineHeight: 1.4,
                          userSelect: 'none',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => setOacChecklist({ ...oacChecklist, [key]: e.target.checked })}
                          disabled={oacSubmitting}
                          style={{
                            marginTop: '2px',
                            cursor: 'pointer',
                            accentColor: '#d9fb06',
                            width: '16px',
                            height: '16px',
                            flexShrink: 0,
                          }}
                        />
                        <span>{label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="avatar-modal-actions" style={{ marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="avatar-modal-btn avatar-modal-btn-cancel"
                  onClick={() => setShowOacModal(false)}
                  disabled={oacSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="avatar-modal-btn avatar-modal-btn-confirm"
                  disabled={
                    (!oacForm.releaseId && userReleases.length === 0 && qualifyingReleases.length === 0) ||
                    !validateYouTubeTopicUrl(oacForm.youtubeTopicUrl).valid ||
                    !validateYouTubeChannelUrl(oacForm.youtubeChannelUrl).valid ||
                    !Object.values(oacChecklist).every(Boolean) ||
                    oacSubmitting
                  }
                  style={{
                    background:
                      (!oacForm.releaseId && userReleases.length === 0 && qualifyingReleases.length === 0) ||
                      !validateYouTubeTopicUrl(oacForm.youtubeTopicUrl).valid ||
                      !validateYouTubeChannelUrl(oacForm.youtubeChannelUrl).valid ||
                      !Object.values(oacChecklist).every(Boolean) ||
                      oacSubmitting
                        ? 'rgba(255,255,255,0.1)'
                        : '#d9fb06',
                    color:
                      (!oacForm.releaseId && userReleases.length === 0 && qualifyingReleases.length === 0) ||
                      !validateYouTubeTopicUrl(oacForm.youtubeTopicUrl).valid ||
                      !validateYouTubeChannelUrl(oacForm.youtubeChannelUrl).valid ||
                      !Object.values(oacChecklist).every(Boolean) ||
                      oacSubmitting
                        ? 'rgba(255,255,255,0.4)'
                        : '#111',
                    cursor:
                      (!oacForm.releaseId && userReleases.length === 0 && qualifyingReleases.length === 0) ||
                      !validateYouTubeTopicUrl(oacForm.youtubeTopicUrl).valid ||
                      !validateYouTubeChannelUrl(oacForm.youtubeChannelUrl).valid ||
                      !Object.values(oacChecklist).every(Boolean) ||
                      oacSubmitting
                        ? 'not-allowed'
                        : 'pointer',
                  }}
                >
                  {oacSubmitting ? 'Submitting Request...' : 'Submit OAC Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
